import { format } from "date-fns";
import { ApplicationMode, AppMode } from "models/appMode";
import dayjs from "dayjs";
import { fetchFile } from "@ffmpeg/util";
import { getFFmpegInstance } from "./ffmpegInstance";
import copy from "copy-to-clipboard";
import { toast } from "react-toastify";
import { AppLog } from "services";
import { StatusMsg } from "utils";
import heic2any from "heic2any";

type SupportedFormat = "jpeg" | "webp" | "png" | "avif";

export class Service {
  static isAuthenticated: boolean = false;
  static userAuth: any = "";
  static token: string | null;
  static authenticationKey: string | null;

  static appKey: string = "LyCqMkT2uxL1JAy9UofOmhCnBeJCEblWYOrfHWCL";

  static applicationMode: AppMode = new AppMode(process.env.REACT_APP_ENVIRONMENT === "production" ? ApplicationMode.RELEASE : ApplicationMode.DEBUG);

  static getSynchID = async (): Promise<string> => {
    const length = 50;
    const chars = "AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQqRrSsTtUuVvWwXxYyZz1234567890";
    let synchId = "";

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * chars.length);
      synchId += chars[randomIndex];
    }

    // Format the current date and time using date-fns
    const strTime = format(new Date(), "yyyyMMddHHmmss");
    synchId = synchId.substring(0, length - strTime.length) + strTime;

    return synchId;
  };

  static generateUUID = (): string => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  //* ======= Convertor ======= * //

  static toInteger(value: any): number {
    if (!value) return 0;
    value = value.toString().replace(/,/g, "").replace(/\s/g, "");
    const result = parseInt(value, 10);
    return isNaN(result) ? 0 : result;
  }

  static toDouble(value: any): number {
    if (!value) return 0.0;
    value = value.toString().replace(/,/g, "").replace(/\s/g, "");
    const result = parseFloat(value);
    return isNaN(result) ? 0.0 : result;
  }

  static replaceText(value: any, replaceText: string | string[], replaceValue: string): string {
    if (!value) return "";

    let stringValue = String(value);

    const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escape special characters

    if (Array.isArray(replaceText)) {
      replaceText.forEach((text) => {
        const escapedText = escapeRegExp(text);
        stringValue = stringValue.replace(new RegExp(escapedText, "g"), replaceValue);
      });
    } else {
      const escapedText = escapeRegExp(replaceText);
      stringValue = stringValue.replace(new RegExp(escapedText, "g"), replaceValue);
    }

    return stringValue;
  }

  static moveToFirstNewArray<T>(array: T[], value: T): T[] {
    return [value, ...array.filter((item) => item !== value)];
  }

  static formatSoundDuration(duration: number): string {
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  static toDateTimeStr(datetime: any): string {
    if (!datetime || !dayjs(datetime).isValid()) return "";
    return dayjs(datetime).format("YYYY-MM-DD HH:mm:ss");
  }

  static toDateTime(value: any): Date {
    const fallbackDate = new Date("1900-01-01T00:00:00");

    if (value === null || value === undefined || value === "null" || value === "" || value === "0000-00-00 00:00:00" || value === "1900-01-01") {
      return fallbackDate;
    }

    const str = value.toString().trim();

    try {
      // Example: '21-Jun-2025' (length 11)
      if (str.length === 11) {
        const parsed = dayjs(`${str} 00:00:00`, "DD-MMM-YYYY HH:mm:ss", true);
        return parsed.isValid() ? parsed.toDate() : fallbackDate;
      }

      // Example: '2025-06-21' (length 10)
      if (str.length === 10) {
        const parsed = dayjs(`${str} 00:00:00`, "YYYY-MM-DD HH:mm:ss", true);
        return parsed.isValid() ? parsed.toDate() : fallbackDate;
      }

      // Try dayjs native parse
      const native = dayjs(str);
      if (native.isValid()) {
        return native.toDate();
      }

      // Fallback custom format
      const customParsed = dayjs(str, "DD-MMM-YYYY HH:mm:ss", true);
      return customParsed.isValid() ? customParsed.toDate() : fallbackDate;
    } catch {
      return fallbackDate;
    }
  }

  static toTime(value: any): { hour: number; minute: number } {
    const fallback = { hour: 0, minute: 0 };

    try {
      if (value === null || value === undefined || value === "null" || value === "" || typeof value !== "string") {
        return fallback;
      }

      const timeStr = value.trim();

      // Match formats: "HH:mm:ss", "HH:mm", "hh:mm A", "hh:mm:ss A"
      const match = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s?(AM|PM)?$/i);
      if (!match) return fallback;

      let hour = parseInt(match[1], 10);
      const minute = parseInt(match[2], 10);
      const ampm = match[4]?.toUpperCase();

      if (isNaN(hour) || isNaN(minute)) return fallback;
      if (minute > 59 || hour > 24) return fallback;

      // Convert to 24-hour format if AM/PM is provided
      if (ampm === "PM" && hour < 12) hour += 12;
      if (ampm === "AM" && hour === 12) hour = 0;

      return { hour, minute };
    } catch {
      return fallback;
    }
  }

  static abbreviateNumber(number: any): string {
    let num = this.toInteger(number);
    if (num >= 1_000_000) {
      return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    }
    if (num >= 1_000) {
      return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    }
    return num.toString();
  }

  static async getThumbnails(file: File | Blob, count: number = 5): Promise<string[] | null> {
    try {
      const ffmpeg = await getFFmpegInstance();

      const videoUrl = URL.createObjectURL(file);
      const duration = await new Promise<number>((resolve) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.src = videoUrl;
        video.onloadedmetadata = () => resolve(video.duration || 0);
        video.onerror = () => resolve(0);
      });
      URL.revokeObjectURL(videoUrl); // cleanup

      if (!duration || duration <= 0) throw new Error("Could not determine video duration.");

      const timestamps = new Set<number>();
      while (timestamps.size < count) {
        const rand = Math.random() * duration;
        if (rand > 0.5 && rand < duration - 0.5) {
          timestamps.add(rand);
        }
      }

      const sortedTimestamps = Array.from(timestamps).sort((a, b) => a - b);

      // ✅ Step 3: Generate thumbnails using FFmpeg
      const inputName = "input.mp4";
      const thumbnails: string[] = [];

      await ffmpeg.writeFile(inputName, await fetchFile(file));

      for (let i = 0; i < sortedTimestamps.length; i++) {
        const time = sortedTimestamps[i].toFixed(2);
        const outputName = `thumb-${i}.jpg`;

        await ffmpeg.exec(["-ss", time, "-i", inputName, "-frames:v", "1", "-vf", "scale=160:-1", "-q:v", "5", outputName]);

        const data = await ffmpeg.readFile(outputName);
        if (typeof data === "string") throw new Error("Expected Uint8Array from ffmpeg.readFile but received string.");
        const blob = new Blob([new Uint8Array(data)], { type: "image/jpeg" });

        const url = URL.createObjectURL(blob);
        thumbnails.push(url);

        await ffmpeg.deleteFile(outputName);
      }

      await ffmpeg.deleteFile(inputName);

      return thumbnails;
    } catch (error) {
      AppLog.show({
        message: `[getThumbnails]`,
        status: StatusMsg.error,
        error,
      });
      return null;
    }
  }

  static async combineVideoWithAudioUrl(video: File | Blob, audioUrl: string): Promise<string | null> {
    const ffmpeg = await getFFmpegInstance();

    const videoName = "video.mp4";
    const audioName = "audio.m4a";
    const outputName = "output.mp4";

    try {
      // 🔈 Fetch audio
      const audioBlob = await fetch(audioUrl).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch audio");
        return res.blob();
      });

      // 📝 Write files to FFmpeg FS
      await ffmpeg.writeFile(videoName, await fetchFile(video));
      await ffmpeg.writeFile(audioName, await fetchFile(audioBlob));

      // 🛠️ Combine with stream mapping (reliable!)
      await ffmpeg.exec(["-i", videoName, "-i", audioName, "-map", "0:v:0", "-map", "1:a:0", "-c:v", "copy", "-c:a", "aac", "-shortest", outputName]);

      const data = await ffmpeg.readFile(outputName);
      if (typeof data === "string") throw new Error("Expected Uint8Array from ffmpeg.readFile but received string.");
      const blob = new Blob([new Uint8Array(data)], { type: "video/mp4" });
      const url = URL.createObjectURL(blob);

      // 🧹 Clean up
      await ffmpeg.deleteFile(videoName);
      await ffmpeg.deleteFile(audioName);
      await ffmpeg.deleteFile(outputName);

      return url;
    } catch (error) {
      AppLog.show({
        message: `[combineVideoWithAudioUrl]`,
        status: StatusMsg.error,
        error,
      });

      return null;
    }
  }

  static async blobUrlToFile(blobUrl: string, filename: string): Promise<File> {
    const res = await fetch(blobUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type });
  }

  static async getVideoMetadata(videoUrl: string): Promise<{
    duration: number;
    width: number;
    height: number;
  }> {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.src = videoUrl;

      video.onloadedmetadata = () => {
        resolve({
          duration: video.duration || 0,
          width: video.videoWidth,
          height: video.videoHeight,
        });
      };

      video.onerror = () => {
        resolve({
          duration: 0,
          width: 0,
          height: 0,
        });
      };
    });
  }

  static getImageDimensionsFromUrl(url: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;

      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = (err) => {
        reject(err);
      };
    });
  }

  static getImageDimensionsFromFile(file: File): Promise<{ width: number; height: number }> | null {
    try {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
          resolve({ width: img.width, height: img.height });
          URL.revokeObjectURL(url);
        };

        img.onerror = (err) => {
          reject(err);
          URL.revokeObjectURL(url);
        };

        img.src = url;
      });
    } catch (error) {
      AppLog.show({
        message: `[getImageDimensionsFromFile]`,
        status: StatusMsg.error,
        error,
      });
      return null;
    }
  }

  static async compressJpeg(
    imageFile: File,
    options: {
      maxWidth?: number;
      maxHeight?: number;
      quality?: number;
    } = {}
  ): Promise<File | null> {
    try {
      const { maxWidth = 200, maxHeight = 200, quality = 5 } = options;

      // 🧠 Extract correct extension based on MIME type
      const mime = imageFile.type;
      const extension = mime.split("/")[1]?.toLowerCase() || "jpg";
      const inputName = `input.${extension}`;
      const outputName = "output.jpg";

      // ✅ Validate supported input types
      if (!["jpeg", "jpg", "png", "webp"].includes(extension)) {
        throw new Error(`Unsupported image type: ${mime}`);
      }

      const ffmpeg = await getFFmpegInstance();

      await ffmpeg.writeFile(inputName, await fetchFile(imageFile));

      const scaleFilter = `scale='min(iw,${maxWidth})':'min(ih,${maxHeight})'`;

      await ffmpeg.exec(["-i", inputName, "-vf", scaleFilter, "-q:v", quality.toString(), outputName]);

      const data = await ffmpeg.readFile(outputName);

      if (typeof data === "string") {
        throw new Error("Expected Uint8Array from ffmpeg.readFile but received string.");
      }

      const file = new File([new Uint8Array(data)], "compressed.jpg", { type: "image/jpeg" });

      // Optional: Clean up in-memory FS (recommended for repeated use)
      await ffmpeg.deleteFile(inputName);
      await ffmpeg.deleteFile(outputName);

      return file;
    } catch (error) {
      AppLog.show({
        message: `[compressJpeg]`,
        status: StatusMsg.error,
        error,
      });
      return null;
    }
  }

  static async copyText(text: string) {
    if (typeof window === "undefined") {
      console.error("Not in a browser environment");
      return;
    }

    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        console.log("Copied to clipboard!");
        return;
      } catch (err) {
        console.error("Clipboard copy failed:", err);
      }
    }

    // fallback
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      document.execCommand("copy");
      console.log("Copied with fallback!");
    } catch (err) {
      console.error("Fallback copy failed:", err);
    }

    document.body.removeChild(textarea);
  }

  static async copyClipboard(text: string) {
    try {
      const success = copy(text);
      if (success) {
        toast.success("Link copied!", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true,
        });
      } else {
        console.error("Failed to copy.");
      }
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  }

  static getSafeScaleRatioForMax(originalWidth: number, originalHeight: number, maxSize: number): number {
    const longSide = Math.max(originalWidth, originalHeight);

    if (longSide <= maxSize) {
      return 1;
    }

    const ratio = maxSize / longSide;
    return parseFloat(ratio.toFixed(4));
  }

  static currencyFormat(value: any): string {
    if (value === null || value === undefined || value === "") return "0.000";

    // Ensure value is a string and remove commas
    const strValue = String(value).replace(/,/g, "");

    // Split integer and decimal parts
    let [integer, decimal = ""] = strValue.split(".");

    // Remove any non-digit characters (safety)
    integer = integer.replace(/\D/g, "");

    // Add thousands separator
    const withCommas = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Format decimal to exactly 2 digits
    decimal = (decimal + "000").slice(0, 3);

    return `${withCommas}.${decimal}`;
  }

  static formatTime = (time: string | null) => {
    if (!time) return ""; // Handle null or empty values

    const [hours, minutes] = time.split(":").map(Number); // Split time into hours and minutes
    const period = hours >= 12 ? "PM" : "AM"; // Determine AM or PM
    const formattedHours = hours % 12 || 12; // Convert to 12-hour format

    return `${formattedHours}:${minutes?.toString().padStart(2, "0")} ${period}`;
  };

  static currencyFormat2(value: any): string {
    // if (value === null || value === undefined || value === "") return "0.00";

    // Ensure value is a string and remove commas
    const strValue = String(value).replace(/,/g, "");

    // Split integer and decimal parts
    let [integer, decimal = ""] = strValue.split(".");

    // Remove any non-digit characters (safety)
    integer = integer.replace(/\D/g, "");

    // Add thousands separator
    const withCommas = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Format decimal to exactly 2 digits
    // decimal = (decimal + "00").slice(0, 3);

    return `${withCommas}.${decimal}`;
  }

  static currencyFormat3(value: any): string {
    if (value === null || value === undefined || value === "") return "0.00";

    // Ensure value is a string and remove commas
    const strValue = String(value).replace(/,/g, "");

    // Split integer and decimal parts
    let [integer, decimal = ""] = strValue.split(".");

    // Remove any non-digit characters (safety)
    integer = integer.replace(/\D/g, "");

    // Add thousands separator
    const withCommas = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Format decimal to exactly 2 digits
    decimal = (decimal + "00").slice(0, 3);

    return `${withCommas}.${decimal}`;
  }

  static async compressImageForUpload(file: File, format: SupportedFormat = "jpeg"): Promise<File | null> {
    try {
      const imageBitmap = await createImageBitmap(file);

      const canvas = document.createElement("canvas");
      canvas.width = imageBitmap.width;
      canvas.height = imageBitmap.height;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas context is null");

      // Set white background if converting from transparent format
      if (file.type === "image/png" && (format === "jpeg" || format === "avif")) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(imageBitmap, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      let encoded: ArrayBuffer;
      let mimeType: string;
      let ext: string;

      switch (format) {
        case "jpeg":
          const { encode: encodeJPEG } = await import("@jsquash/jpeg");
          encoded = await encodeJPEG(imageData, { quality: 75 });
          mimeType = "image/jpeg";
          ext = "jpg";
          break;

        case "webp":
          const { encode: encodeWebP } = await import("@jsquash/webp");
          encoded = await encodeWebP(imageData, { quality: 75 });
          mimeType = "image/webp";
          ext = "webp";
          break;

        case "png":
          const { encode: encodePNG } = await import("@jsquash/png");
          encoded = await encodePNG(imageData);
          mimeType = "image/png";
          ext = "png";
          break;

        case "avif":
          const { encode: encodeAVIF } = await import("@jsquash/avif");
          encoded = await encodeAVIF(imageData, { quality: 50 });
          mimeType = "image/avif";
          ext = "avif";
          break;

        default:
          throw new Error("Unsupported format");
      }

      return new File([encoded], `compressed.${ext}`, { type: mimeType });
    } catch (error) {
      AppLog.show({
        message: `[compressImageForUpload]`,
        status: StatusMsg.error,
        error,
      });
      return null;
    }
  }

  static async compressImageToThumbnailForUpload(file: File): Promise<File | null> {
    try {
      const imageBitmap = await createImageBitmap(file);

      // 🔧 Resize to 200px width, maintaining aspect ratio
      const targetWidth = 200;
      const aspectRatio = imageBitmap.height / imageBitmap.width;
      const targetHeight = Math.round(targetWidth * aspectRatio);

      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas context is null");

      // Optional: add white background for PNG transparency → JPEG
      if (file.type === "image/png" || file.name.toLowerCase().endsWith(".png")) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(imageBitmap, 0, 0, targetWidth, targetHeight);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const { encode: encodeJPEG } = await import("@jsquash/jpeg");
      const encoded: ArrayBuffer = await encodeJPEG(imageData, { quality: 50 });
      const mimeType = "image/jpeg";
      const ext = "jpg";

      return new File([encoded], `thumbnail_compressed.${ext}`, { type: mimeType });
    } catch (error) {
      AppLog.show({
        message: `[compressImageToThumbnailForUpload]`,
        status: StatusMsg.error,
        error,
      });
      return null;
    }
  }

  static async convertHeicToJpeg(file: File): Promise<File | null> {
    try {
      if (file.type === "image/heic" || file.type === "image/heif" || file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif")) {
        const resultBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.9,
        });

        return new File([resultBlob as Blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
          type: "image/jpeg",
        });
      }

      return file;
    } catch (error) {
      AppLog.show({
        message: `[convertHeicToJpeg]`,
        status: StatusMsg.error,
        error,
      });
      return null;
    }
  }

  // * ======  Formater ===== * //

  static formatDateTime(value: any, { format }: { format?: string } = {}): string {
    const datetime = this.toDateTime(value);
    return dayjs(datetime).format(format ?? "YYYY-MM-DD HH:mm:ss");
  }

  static formatTimeJK(value: any, { format }: { format?: string } = {}): string {
    const { hour, minute } = this.toTime(value);

    const timeDate = new Date();
    timeDate.setHours(hour);
    timeDate.setMinutes(minute);
    timeDate.setSeconds(0);
    timeDate.setMilliseconds(0);

    return dayjs(timeDate).format(format ?? "hh:mm A");
  }
}

